import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    type SpendInsightsLastRequestParams,
    type SpendProductSupplierRowDto,
    type SpendProductSuppliersResponseDto,
    buildSpendInsightsBody,
} from 'entities/ReportSpendSummary';
import { $api } from 'shared/api/api';
import { toNumber } from 'shared/lib/number/number';
import {
    type SpendDrillInScope,
    buildSpendDrillInRequestParams,
} from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

interface UseSpendProductSuppliersParams {
    sku: string | undefined;
    lastRequestParams: SpendInsightsLastRequestParams | null;
    scope?: SpendDrillInScope;
}

interface UseSpendProductSuppliersResult {
    suppliers: SpendProductSupplierRowDto[];
    suppliersLoading: boolean;
    suppliersTotalSpend: number;
}

export const useSpendProductSuppliers = ({
    sku,
    lastRequestParams,
    scope,
}: UseSpendProductSuppliersParams): UseSpendProductSuppliersResult => {
    const [suppliers, setSuppliers] = useState<SpendProductSupplierRowDto[]>(
        [],
    );
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const activeRequestControllerRef = useRef<AbortController | null>(null);

    const loadSuppliers = useCallback(async () => {
        if (!sku || !lastRequestParams) {
            setSuppliers([]);
            return;
        }

        activeRequestControllerRef.current?.abort();
        const controller = new AbortController();
        activeRequestControllerRef.current = controller;
        setSuppliersLoading(true);

        try {
            const response = await $api.post<SpendProductSuppliersResponseDto>(
                `/v1/reports/spend-insights/products/${encodeURIComponent(
                    sku,
                )}/suppliers`,
                buildSpendInsightsBody(
                    buildSpendDrillInRequestParams({
                        lastRequestParams,
                        scope,
                    }),
                ),
                { signal: controller.signal },
            );
            setSuppliers(response.data.suppliers ?? []);
        } catch (requestError) {
            if (!axios.isCancel(requestError)) {
                setSuppliers([]);
            }
        } finally {
            if (activeRequestControllerRef.current === controller) {
                activeRequestControllerRef.current = null;
                setSuppliersLoading(false);
            }
        }
    }, [lastRequestParams, scope, sku]);

    useEffect(() => {
        void loadSuppliers();

        return () => {
            activeRequestControllerRef.current?.abort();
            activeRequestControllerRef.current = null;
        };
    }, [loadSuppliers]);

    const suppliersTotalSpend = useMemo(
        () => suppliers.reduce((sum, row) => sum + toNumber(row.totalSpend), 0),
        [suppliers],
    );

    return { suppliers, suppliersLoading, suppliersTotalSpend };
};
